import React, { useState, useEffect } from 'react';
import { dashboardService } from '../../services/dashboardService';
import { normalizeDocumentStatus } from '../../utils/workflowConstants';

interface StatusCount {
  status: string;
  count: number;
  color: string;
}

const ALL_STATUSES: StatusCount[] = [
  { status: 'edit', count: 0, color: 'bg-gray-500' },
  { status: 'review', count: 0, color: 'bg-yellow-500' },
  { status: 'signature', count: 0, color: 'bg-indigo-500' },
  { status: 'published', count: 0, color: 'bg-green-500' },
  { status: 'archived', count: 0, color: 'bg-purple-500' },
];

const STATUS_LABELS: Record<string, string> = {
  edit: 'Em edição',
  review: 'Em revisão',
  signature: 'Assinatura',
  published: 'Publicado',
  archived: 'Arquivado',
};

export const DocumentsByStatus: React.FC = () => {
  const [statusData, setStatusData] = useState<StatusCount[]>(ALL_STATUSES);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);

  useEffect(() => {
    fetchDocumentsByStatus();
  }, []);

  const fetchDocumentsByStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Buscar estatísticas de documentos da API
      const documentStats = await dashboardService.getDocumentStatistics();
      
      // Mapear os dados da API para o formato local
      const fromApi = documentStats.documentsByStatus ?? [];
      const updatedStatusData = ALL_STATUSES.map(row => {
        const count = fromApi
          .filter(s => normalizeDocumentStatus(s.status) === row.status)
          .reduce((sum, s) => sum + (s.count || 0), 0);
        return { ...row, count };
      });
      
      setStatusData(updatedStatusData);
      setTotalDocuments(documentStats.totalDocuments || 0);
    } catch (err) {
      console.error('Erro ao buscar documentos por status:', err);
      setError('Não foi possível carregar os dados de status dos documentos.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculatePercentage = (count: number) => {
    return totalDocuments > 0 ? Math.round((count / totalDocuments) * 100) : 0;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Documentos por Status</h2>
        <button 
          onClick={fetchDocumentsByStatus}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Atualizar
        </button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-4">{error}</div>
      ) : (
        <>
          <div className="mb-4 text-center">
            <span className="text-sm text-gray-600">Total: {totalDocuments} documentos</span>
          </div>
          
          <div className="space-y-4">
            {statusData.map((item) => {
              const percentage = calculatePercentage(item.count);
              return (
                <div key={item.status} className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{STATUS_LABELS[item.status]}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">{item.count}</span>
                      <span className="text-xs text-gray-500">({percentage}%)</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className={`${item.color} h-2.5 rounded-full transition-all duration-300`} 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      
      <div className="mt-4 pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Atualiza a cada 5 minutos • Todos os status são exibidos
        </p>
      </div>
    </div>
  );
};